/* eslint-disable react-refresh/only-export-components */
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80',
        outline: 'text-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

// ── NEW — status pill family (reproduces legacy variantStyles VERBATIM) ───────
// Base = legacy StatusChip base (StatusChip.tsx:84-97), minus the icon-gap
// (applied by the wrapper only when an icon is present) and minus `truncate`
// (applied to the inner <span>, not the container).
const statusBadgeVariants = cva(
  'inline-flex w-fit items-center justify-center rounded-full border px-2 py-1 text-[10px] font-semibold uppercase',
  {
    variants: {
      variant: {
        info: 'bg-chariot-stroke-info border-chariot-stroke-info text-white',
        high: 'bg-chariot-stroke-high border-chariot-stroke-high text-white',
        critical:
          'bg-chariot-stroke-critical border-chariot-stroke-critical text-white',
        neutral:
          'bg-chariot-stroke-low border-chariot-stroke-low text-white dark:bg-chariot-stroke dark:border-chariot-stroke',
        muted:
          'bg-chariot-background-secondary border-chariot-stroke text-chariot-text-primary',
        default:
          'bg-chariot-background-secondary border-chariot-stroke text-chariot-text-primary',
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

// Canonical variant type, derived from the CVA (single source of truth).
export type StatusChipVariant = NonNullable<
  VariantProps<typeof statusBadgeVariants>['variant']
>;

export { Badge, badgeVariants, statusBadgeVariants };
